import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight, CircleAlert, HardDrive, MapPin, Video } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../components/Button';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import {
  accountLabel,
  allSites,
  byClient,
  channelsKey,
  devicesKey,
  isRecorder,
  listChannels,
  listDevices,
  sitesKey,
  type Device,
  type Site,
} from '../inventory/api';
import {
  accessKey,
  accessMessages,
  getAccess,
  putAccess,
  type AccessMode,
  type RoleKind,
  type UserAccess,
} from './api';

interface Props {
  userId: string;
  name: string;
  isAdmin: boolean;
  /** Kind of the user's profile and its client (ADR-027). */
  kind?: RoleKind | undefined;
  client?: { id: string; name: string } | null;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

/**
 * Which cameras a user sees (ADR-019): every camera of the organisation, or
 * only whole sites and chosen cameras (a recorder brings its channels).
 */
export function CameraAccess({ userId, name, isAdmin, kind, client = null }: Props) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const wholeClient = kind === 'client' && client !== null;
  const saved = useQuery({
    queryKey: accessKey(userId),
    queryFn: () => getAccess(request, userId),
    enabled: !isAdmin && !wholeClient,
  });
  const [draft, setDraft] = useState<UserAccess | null>(null);
  const current = draft ?? saved.data ?? null;

  const save = useMutation({
    mutationFn: (body: UserAccess) => putAccess(request, userId, body),
    onSuccess: (a) => {
      qc.setQueryData(accessKey(userId), a);
      setDraft(null);
      notify(
        a.mode === 'all'
          ? `${name} vê todas as câmeras.`
          : `Acesso de ${name} atualizado: ${String((a.client_ids ?? []).length)} clientes, ${String(a.site_ids.length)} contas e ${String(a.device_ids.length)} câmeras.`,
      );
    },
  });
  const error =
    save.error instanceof ApiError && save.error.status === 422
      ? (Object.values(save.error.fields).map((c) => accessMessages[c] ?? 'Valor inválido.')[0] ??
        null)
      : save.error
        ? 'Não foi possível salvar o acesso. Tente novamente.'
        : null;

  const dirty =
    !!draft &&
    !!saved.data &&
    (draft.mode !== saved.data.mode ||
      !sameSet(draft.site_ids, saved.data.site_ids) ||
      !sameSet(draft.client_ids ?? [], saved.data.client_ids ?? []) ||
      !sameSet(draft.device_ids, saved.data.device_ids));
  const edit = (patch: Partial<UserAccess>) => {
    if (current) setDraft({ ...current, ...patch });
  };
  const toggle = (key: 'client_ids' | 'site_ids' | 'device_ids', id: string, on: boolean) => {
    if (!current) return;
    const set = new Set(current[key]);
    if (on) set.add(id);
    else set.delete(id);
    edit({ [key]: [...set] });
  };

  return (
    <section aria-labelledby="cameras-heading" className="mt-6 border-t border-line pt-5">
      <h3 id="cameras-heading" className="text-[13px] font-medium text-fg">
        Câmeras
      </h3>
      {isAdmin ? (
        <p className="mt-0.5 text-xs text-fg-muted">
          Administradores sempre veem todas as câmeras da organização.
        </p>
      ) : wholeClient ? (
        <p className="mt-0.5 text-xs text-fg-muted">
          Vê todas as contas ativas de <span className="text-fg">{client.name}</span>, inclusive as
          criadas depois. Contas ou clientes suspensos ficam de fora.
        </p>
      ) : saved.isError ? (
        <p role="alert" className="mt-2 flex items-center gap-1.5 text-xs text-crit">
          <CircleAlert aria-hidden="true" className="size-3.5" />
          Não foi possível carregar o acesso às câmeras.
        </p>
      ) : !current ? (
        <p role="status" className="mt-2 text-xs text-fg-muted">
          Carregando…
        </p>
      ) : (
        <>
          {client ? (
            <p className="mt-0.5 text-xs text-fg-muted">
              Só contas e câmeras de <span className="text-fg">{client.name}</span>.
            </p>
          ) : (
            <div
              role="radiogroup"
              aria-label="Câmeras que o usuário vê"
              className="mt-2 space-y-1.5"
            >
              {(
                [
                  ['all', 'Todas as câmeras da organização'],
                  ['assigned', 'Somente os locais e câmeras selecionados'],
                ] as [AccessMode, string][]
              ).map(([mode, label]) => (
                <label
                  key={mode}
                  className="flex cursor-pointer items-center gap-2 text-[13px] text-fg"
                >
                  <input
                    type="radio"
                    name={`access-${userId}`}
                    checked={current.mode === mode}
                    onChange={() => {
                      edit({ mode });
                    }}
                    className="size-3.5 accent-[var(--color-accent-strong)]"
                  />
                  {label}
                </label>
              ))}
            </div>
          )}
          {(current.mode === 'assigned' || client) && (
            <>
              <p className="mt-2 text-xs text-fg-muted">
                Usuários restritos só assistem (ao vivo, reprodução e eventos das suas câmeras); não
                gerenciam dispositivos nem contas. Marcar o cliente inclui todas as contas dele, e
                marcar a conta inclui as câmeras cadastradas nela depois. Contas ou clientes
                suspensos ficam fora do acesso.
              </p>
              <SiteTree
                {...(client ? { onlyClient: client.id } : {})}
                clientIds={current.client_ids ?? []}
                onClient={(id, on) => {
                  toggle('client_ids', id, on);
                }}
                siteIds={current.site_ids}
                deviceIds={current.device_ids}
                onSite={(id, on) => {
                  toggle('site_ids', id, on);
                }}
                onDevice={(id, on) => {
                  toggle('device_ids', id, on);
                }}
              />
              {(current.client_ids ?? []).length +
                current.site_ids.length +
                current.device_ids.length ===
                0 && (
                <p className="mt-2 text-xs text-warn">
                  Nada selecionado: o usuário não verá nenhuma câmera.
                </p>
              )}
            </>
          )}
          {error && (
            <p role="alert" className="mt-2 text-xs text-crit">
              {error}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <Button
              disabled={!dirty || save.isPending}
              onClick={() => {
                save.mutate(client ? { ...current, mode: 'assigned' } : current);
              }}
            >
              {save.isPending ? 'Salvando…' : 'Salvar acesso'}
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                  save.reset();
                }}
              >
                Desfazer
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

interface TreeProps {
  /** Only the accounts of this client (people of a client, ADR-027). */
  onlyClient?: string;
  /** Whole clients: every account, including later ones (ADR-026). */
  clientIds?: string[];
  siteIds: string[];
  deviceIds: string[];
  onClient?: (id: string, on: boolean) => void;
  onSite: (id: string, on: boolean) => void;
  onDevice: (id: string, on: boolean) => void;
}

/** Clients → accounts → cameras, to grant access at any level. */
export function SiteTree({
  onlyClient,
  clientIds = [],
  siteIds,
  deviceIds,
  onClient,
  onSite,
  onDevice,
}: TreeProps) {
  const { request } = useAuth();
  const sites = useQuery({ queryKey: [...sitesKey, 'all'], queryFn: () => allSites(request) });
  if (sites.isPending)
    return (
      <p role="status" className="mt-2 text-xs text-fg-muted">
        Carregando clientes e contas…
      </p>
    );
  if (sites.isError)
    return (
      <p role="alert" className="mt-2 text-xs text-crit">
        Não foi possível carregar as contas.
      </p>
    );
  const groups = byClient(
    onlyClient ? sites.data.filter((s) => s.client.id === onlyClient) : sites.data,
  );
  return (
    <ul
      aria-label="Clientes, contas e câmeras"
      className="mt-2 max-h-96 divide-y divide-line overflow-y-auto rounded-md border border-line"
    >
      {groups.map((g) => {
        const clientId = g.sites[0]?.client.id ?? '';
        return (
          <ClientNode
            key={clientId}
            name={g.client}
            sites={g.sites}
            whole={clientIds.includes(clientId)}
            startOpen={groups.length <= 10}
            {...(onClient
              ? {
                  onClient: (on: boolean) => {
                    onClient(clientId, on);
                  },
                }
              : {})}
            siteIds={siteIds}
            deviceIds={deviceIds}
            onSite={onSite}
            onDevice={onDevice}
          />
        );
      })}
    </ul>
  );
}

function ClientNode({
  name,
  sites,
  whole,
  startOpen,
  onClient,
  siteIds,
  deviceIds,
  onSite,
  onDevice,
}: {
  name: string;
  sites: Site[];
  whole: boolean;
  startOpen: boolean;
  onClient?: (on: boolean) => void;
} & Omit<TreeProps, 'clientIds' | 'onClient'>) {
  const [open, setOpen] = useState(startOpen);
  const picked = sites.filter((s) => siteIds.includes(s.id)).length;
  return (
    <li>
      <div className="flex items-center gap-2 bg-canvas px-2 py-1.5">
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Recolher' : 'Expandir'} cliente ${name}`}
          onClick={() => {
            setOpen((o) => !o);
          }}
          className="rounded p-0.5 text-fg-subtle hover:bg-surface-hover"
        >
          <ChevronRight
            aria-hidden="true"
            className={`size-4 transition-transform ${open ? 'rotate-90' : ''}`}
          />
        </button>
        <label
          className={`flex min-w-0 flex-1 items-center gap-2 text-[13px] font-medium text-fg ${onClient ? 'cursor-pointer' : ''}`}
        >
          {onClient && (
            <input
              type="checkbox"
              aria-label={`Cliente ${name} inteiro`}
              checked={whole}
              onChange={(e) => {
                onClient(e.target.checked);
              }}
              className="size-3.5 accent-[var(--color-accent-strong)]"
            />
          )}
          <Building2 aria-hidden="true" className="size-3.5 shrink-0 text-fg-subtle" />
          <span className="truncate">{name}</span>
          <span className="ml-auto shrink-0 text-2xs font-normal text-fg-subtle">
            {whole
              ? 'Cliente inteiro'
              : picked > 0
                ? `${String(picked)} de ${String(sites.length)} contas`
                : `${String(sites.length)} ${sites.length === 1 ? 'conta' : 'contas'}`}
          </span>
        </label>
      </div>
      {open && (
        <ul aria-label={`Contas de ${name}`} className="pl-5">
          {sites.map((s) => (
            <SiteNode
              key={s.id}
              site={s}
              whole={whole || siteIds.includes(s.id)}
              inherited={whole}
              deviceIds={deviceIds}
              onSite={onSite}
              onDevice={onDevice}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function SiteNode({
  site,
  whole,
  inherited = false,
  deviceIds,
  onSite,
  onDevice,
}: Pick<TreeProps, 'deviceIds' | 'onSite' | 'onDevice'> & {
  site: Site;
  whole: boolean;
  /** Covered by its whole client. */
  inherited?: boolean;
}) {
  const { request } = useAuth();
  const [open, setOpen] = useState(false);
  const devices = useQuery({
    queryKey: [...devicesKey, 'access', site.id],
    queryFn: () =>
      listDevices(request, {
        q: '',
        site: site.id,
        kind: '',
        status: '',
        sort: 'name',
        page: 1,
        size: 100,
        topLevel: true,
      }),
    enabled: open,
  });
  const picked = (devices.data?.items ?? []).filter((d) => deviceIds.includes(d.id)).length;
  return (
    <li>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Recolher' : 'Expandir'} ${accountLabel(site.client.name, site.name)}`}
          onClick={() => {
            setOpen((o) => !o);
          }}
          className="rounded p-0.5 text-fg-subtle hover:bg-surface-hover"
        >
          <ChevronRight
            aria-hidden="true"
            className={`size-4 transition-transform ${open ? 'rotate-90' : ''}`}
          />
        </button>
        <label
          className={`flex min-w-0 flex-1 items-center gap-2 text-[13px] ${inherited ? 'text-fg-muted' : 'cursor-pointer text-fg'}`}
          title={inherited ? 'Incluída pelo cliente marcado acima.' : undefined}
        >
          <input
            type="checkbox"
            aria-label={accountLabel(site.client.name, site.name)}
            checked={whole}
            disabled={inherited}
            onChange={(e) => {
              onSite(site.id, e.target.checked);
            }}
            className="size-3.5 accent-[var(--color-accent-strong)]"
          />
          <MapPin aria-hidden="true" className="size-3.5 shrink-0 text-fg-subtle" />
          <span className="truncate">{site.name}</span>
          {site.status !== 'active' && (
            <span className="shrink-0 text-2xs text-warn">
              {site.status === 'suspended' ? 'suspensa' : 'cancelada'}
            </span>
          )}
          <span className="ml-auto shrink-0 text-2xs text-fg-subtle">
            {whole ? 'Conta inteira' : picked > 0 ? `${String(picked)} selecionadas` : ''}
          </span>
        </label>
      </div>
      {open && (
        <ul aria-label={`Câmeras de ${site.name}`} className="pb-1.5 pl-9">
          {devices.isPending ? (
            <li className="py-1 text-xs text-fg-muted">Carregando câmeras…</li>
          ) : devices.isError ? (
            <li className="py-1 text-xs text-crit">Não foi possível carregar as câmeras.</li>
          ) : devices.data.items.length === 0 ? (
            <li className="py-1 text-xs text-fg-muted">Nenhum dispositivo neste local.</li>
          ) : (
            devices.data.items.map((d) => (
              <DeviceNode
                key={d.id}
                device={d}
                inherited={whole}
                deviceIds={deviceIds}
                onDevice={onDevice}
              />
            ))
          )}
          {devices.data && devices.data.total > devices.data.items.length && (
            <li className="py-1 text-xs text-fg-muted">
              Mostrando {devices.data.items.length} de {devices.data.total}; marque o local inteiro
              para incluir todas.
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

function DeviceNode({
  device: d,
  inherited,
  deviceIds,
  onDevice,
}: {
  device: Device;
  /** Covered by a grant above (its site, or its recorder). */
  inherited: boolean;
  deviceIds: string[];
  onDevice: (id: string, on: boolean) => void;
}) {
  const { request } = useAuth();
  const recorder = isRecorder(d);
  const [open, setOpen] = useState(false);
  const channels = useQuery({
    queryKey: channelsKey(d.id),
    queryFn: () => listChannels(request, d.id),
    enabled: recorder && open,
  });
  const checked = inherited || deviceIds.includes(d.id);
  const Icon = recorder ? HardDrive : Video;
  return (
    <li>
      <div className="flex items-center gap-2 py-0.5">
        {recorder ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Recolher' : 'Expandir'} canais de ${d.name}`}
            onClick={() => {
              setOpen((o) => !o);
            }}
            className="rounded p-0.5 text-fg-subtle hover:bg-surface-hover"
          >
            <ChevronRight
              aria-hidden="true"
              className={`size-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
            />
          </button>
        ) : (
          <span className="w-[18px]" />
        )}
        <label
          className={`flex min-w-0 flex-1 items-center gap-2 text-xs ${inherited ? 'text-fg-muted' : 'cursor-pointer text-fg'}`}
          title={inherited ? 'Incluída pelo local ou pelo gravador marcado acima.' : undefined}
        >
          <input
            type="checkbox"
            checked={checked}
            disabled={inherited}
            onChange={(e) => {
              onDevice(d.id, e.target.checked);
            }}
            className="size-3.5 accent-[var(--color-accent-strong)]"
          />
          <Icon aria-hidden="true" className="size-3.5 shrink-0 text-fg-subtle" />
          <span className="truncate">{d.name}</span>
          {(d.camera_number ?? null) !== null && (
            <span className="shrink-0 font-mono text-2xs text-fg-subtle">nº {d.camera_number}</span>
          )}
          {recorder && (
            <span className="ml-auto shrink-0 text-2xs text-fg-subtle">
              {d.channel_count} canais
            </span>
          )}
        </label>
      </div>
      {recorder && open && (
        <ul aria-label={`Canais de ${d.name}`} className="pl-6">
          {channels.isPending ? (
            <li className="py-1 text-xs text-fg-muted">Carregando canais…</li>
          ) : channels.isError ? (
            <li className="py-1 text-xs text-crit">Não foi possível carregar os canais.</li>
          ) : (
            channels.data.map((c) => (
              <DeviceNode
                key={c.id}
                device={c}
                inherited={checked}
                deviceIds={deviceIds}
                onDevice={onDevice}
              />
            ))
          )}
        </ul>
      )}
    </li>
  );
}
